import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RENTAL_STATUS, dateTime, money, type RentalBooking, type RentalSpace, type RentalStatus,
} from '@lcc/shared';
import { api, qs } from '../lib/api';
import {
  Card, Empty, ErrorNote, Loading, Modal, TextField, errorMessage, useToast,
} from '../components/ui';
import { PageHeader } from '../App';

export function RentalsPage() {
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date(Date.now() + 120 * 864e5).toISOString().slice(0, 10));
  const [quoting, setQuoting] = useState<RentalBooking | null>(null);

  const spaces = useQuery({
    queryKey: ['rental-spaces'],
    queryFn: () => api.get<{ data: RentalSpace[] }>('/rentals/spaces').then((r) => r.data),
  });
  const bookings = useQuery({
    queryKey: ['rental-bookings', from, to],
    queryFn: () => api.get<{ data: RentalBooking[] }>(`/rentals/bookings${qs({ from, to })}`)
      .then((r) => r.data),
  });

  const enquiries = bookings.data?.filter((b) => b.status === 'enquiry') ?? [];

  return (
    <>
      <PageHeader title="Rentals"
        sub={bookings.data ? `${bookings.data.length} bookings in view` : undefined} />

      <div className="content stack">
        {enquiries.length > 0 && (
          <div className="banner banner--warn">
            <strong>{enquiries.length} enquir{enquiries.length === 1 ? 'y' : 'ies'} awaiting a reply.</strong>{' '}
            An enquiry does not hold the room — confirm or hold it to reserve the date.
          </div>
        )}

        <div className="grid grid--3">
          {spaces.data?.map((s) => (
            <Card key={s.id} title={s.name}>
              <p className="small muted">{s.description}</p>
              <div className="row row--tight small" style={{ marginTop: '.5rem' }}>
                {s.capacity && <span className="pill">{s.capacity} people</span>}
                {s.hourlyCents && <span className="pill">{money(s.hourlyCents)}/hr</span>}
                {s.dailyCents && <span className="pill">{money(s.dailyCents)}/day</span>}
                {s.isAccessible && <span className="pill pill--good">accessible</span>}
              </div>
            </Card>
          ))}
        </div>

        <Card>
          <div className="row">
            <div style={{ flex: '1 1 160px' }}>
              <TextField label="From" type="date" value={from} onChange={setFrom} />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <TextField label="To" type="date" value={to} onChange={setTo} />
            </div>
          </div>
        </Card>

        <Card title="Bookings" flush>
          {bookings.isLoading ? <Loading />
            : bookings.error ? <div style={{ padding: '1rem' }}><ErrorNote error={bookings.error} /></div>
            : !bookings.data?.length ? <Empty title="No bookings in this range" />
            : (
              <div className="table-wrap">
                <table className="t">
                  <thead>
                    <tr><th>When</th><th>Space</th><th>Contact</th><th>Purpose</th>
                      <th>Extras</th><th className="num">Quote</th><th>Status</th><th /></tr>
                  </thead>
                  <tbody>
                    {bookings.data.map((b) => (
                      <BookingRow key={b.id} booking={b} onQuote={() => setQuoting(b)} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </Card>
      </div>

      {quoting && <QuoteModal booking={quoting} onClose={() => setQuoting(null)} />}
    </>
  );
}

function BookingRow({ booking: b, onQuote }: { booking: RentalBooking; onQuote: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const setStatus = useMutation({
    mutationFn: (status: RentalStatus) =>
      api.patch(`/rentals/bookings/${b.id}/status`, { status, quotedCents: null, notes: null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rental-bookings'] });
      toast.success('Booking updated.');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <tr>
      <td className="small nowrap">
        {dateTime(b.startsAt)}<br /><span className="muted">to {dateTime(b.endsAt)}</span>
      </td>
      <td>{b.spaceName}</td>
      <td className="small">
        <b>{b.contactName}</b><br />
        <span className="muted">{b.contactEmail ?? b.contactPhone ?? '—'}</span>
        {b.organisation && <><br /><span className="muted">{b.organisation}</span></>}
      </td>
      <td className="small muted">{b.purpose ?? '—'}</td>
      <td className="row row--tight">
        {b.needsKitchen && <span className="pill">kitchen</span>}
        {b.needsBar && <span className="pill">bar</span>}
        {b.needsLinen && <span className="pill">linen</span>}
      </td>
      <td className="num">{b.quotedCents ? money(b.quotedCents) : '—'}</td>
      <td>
        <select
          value={b.status} disabled={setStatus.isPending}
          onChange={(e) => setStatus.mutate(e.target.value as RentalStatus)}
          style={{ fontSize: '.8rem', padding: '.2rem .35rem' }}
        >
          {RENTAL_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td className="right nowrap">
        <button className="btn btn--sm" type="button" onClick={onQuote}>Quote</button>
      </td>
    </tr>
  );
}

function QuoteModal({ booking, onClose }: { booking: RentalBooking; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [amount, setAmount] = useState(
    booking.quotedCents ? (booking.quotedCents / 100).toFixed(2) : '',
  );
  const [notes, setNotes] = useState(booking.notes ?? '');

  const save = useMutation({
    mutationFn: () => api.patch(`/rentals/bookings/${booking.id}/status`, {
      status: 'confirmed',
      quotedCents: Math.round(Number(amount) * 100),
      notes: notes || null,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rental-bookings'] });
      toast.success('Confirmed and quoted.');
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const invoice = useMutation({
    mutationFn: () => api.post<{ number: number }>(`/rentals/bookings/${booking.id}/invoice`),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['rental-bookings'] });
      toast.success(`Invoice #${r.number} raised.`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Modal
      title={`Quote — ${booking.spaceName}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          {booking.quotedCents && !booking.invoiceId && (
            <button className="btn" type="button" disabled={invoice.isPending}
              onClick={() => invoice.mutate()}>
              {invoice.isPending ? 'Raising…' : 'Raise an invoice'}
            </button>
          )}
          <button className="btn btn--accent" type="button"
            disabled={save.isPending || !amount} onClick={() => save.mutate()}>
            Confirm &amp; quote
          </button>
        </>
      }
    >
      <div className="stack">
        {(save.error || invoice.error) ? <ErrorNote error={save.error ?? invoice.error} /> : null}
        <p className="small muted" style={{ margin: 0 }}>
          {booking.contactName} · {dateTime(booking.startsAt)} to {dateTime(booking.endsAt)}
        </p>
        <TextField label="Quote (CAD)" type="number" value={amount} onChange={setAmount}
          hint="Confirming holds the room — the database refuses an overlapping booking." />
        <TextField label="Notes" value={notes} onChange={setNotes} />
        {booking.invoiceId && (
          <div className="banner banner--good">An invoice has already been raised for this booking.</div>
        )}
      </div>
    </Modal>
  );
}
